/**
 * Custom hook for SupplierDetail state management
 */

import { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  suppliersAPI,
  SupplierProfile,
  purchasingAPI,
  PurchaseOrder,
  qualityAPI,
  NonConformanceReport,
  coreAPI,
  UserProfile,
  UserSitePermission,
  Company,
} from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useView } from '../../contexts/ViewContext';
import { useIsCustomer, usePortalMode } from '../../hooks/usePortalMode';
import { OtifSettings, SupplierDetailHook } from './types';
import { getApiErrorMessage } from '../../utils/errorUtils';

/** Orders per page on the supplier's Orders tab. */
export const ORDERS_PAGE_SIZE = 25;

export function useSupplierDetail(): SupplierDetailHook {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'overview';

  const { startImpersonation } = useAuth();

  // Supplier data state
  const [supplier, setSupplier] = useState<SupplierProfile | null>(null);
  // CR-4815 r6: the embedded supplier.company_details is the slim
  // CompanyNestedSerializer (CWE-359 PII strip) and omits registration_number,
  // primary_email/phone, website and the address_line_* fields the Overview
  // "Company Information" card renders. Fetch the full Company separately so the
  // card reflects what's actually persisted (and refreshes after an edit).
  const [companyInfo, setCompanyInfo] = useState<Company | null>(null);
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  // Orders tab paginates server-side; `ordersCount` is DRF's unpaginated
  // total so the tab can show a real "x–y of N" rather than guessing.
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersCount, setOrdersCount] = useState(0);
  // CR-7028: Supplier code filter for orders
  const [ordersSupplierCode, setOrdersSupplierCode] = useState('');
  const [ncrs, setNCRs] = useState<NonConformanceReport[]>([]);
  const [users, setUsers] = useState<UserProfile[]>([]);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ncrsLoading, setNCRsLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Active tab (honours ?tab=<name> on initial load)
  const [activeTab, setActiveTab] = useState(initialTab);

  // User modal state
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [userModalTab, setUserModalTab] = useState<'profile' | 'sites'>('profile');
  const [userSitePermissions, setUserSitePermissions] = useState<UserSitePermission[]>([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [savingPermission, setSavingPermission] = useState<number | null>(null);
  const [userSearch, setUserSearch] = useState('');
  const [impersonating, setImpersonating] = useState<number | null>(null);

  // OTIF Settings state
  const [otifSettings, setOtifSettings] = useState<OtifSettings | null>(null);
  const [otifLoading, setOtifLoading] = useState(false);
  const [otifSaving, setOtifSaving] = useState(false);

  // CR-5857: Edit + Reset Password dialog state, owned at the page level so
  // both the cards in UsersTab and the action bar in UserModal can trigger
  // the same flow without duplicating it.
  const [editUser, setEditUser] = useState<UserProfile | null>(null);
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [resettingPasswordFor, setResettingPasswordFor] = useState<number | null>(null);

  // Computed values
  const { isHelix: isHelixAdmin } = usePortalMode();
  const isCustomer = useIsCustomer();
  const { user } = useAuth();
  // CR-5857: customer admins (or helix admins) gain Edit + Reset Password.
  // Mirrors UsersManagement's canManageUsers predicate.
  const isCustomerAdmin =
    isHelixAdmin ||
    (isCustomer && Boolean(user?.is_company_admin || user?.can_manage_users));
  // CR-5977 follow-up: pass view scope to the profile endpoint so the
  // detail header's OTD / PPM / NCR aggregates narrow to the same customer
  // set that the OTD drilldown applies — mirrors useSuppliers.ts' pattern
  // for the list page.
  const { viewLevel, getViewQueryParams } = useView();
  const isOwnCompany = !!(supplier && user && user.company === supplier.company);

  // Filter users by search
  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      if (!userSearch) return true;
      const search = userSearch.toLowerCase();
      return (
        u.user.full_name?.toLowerCase().includes(search) ||
        u.user.email.toLowerCase().includes(search) ||
        u.job_title?.toLowerCase().includes(search) ||
        u.role_name?.toLowerCase().includes(search)
      );
    });
  }, [users, userSearch]);

  // Fetch supplier data on mount and whenever the active view changes,
  // so the header KPIs re-narrow when the user switches group/site context.
  const viewParams = getViewQueryParams();
  const viewParamsKey = JSON.stringify({ viewLevel, ...viewParams });
  useEffect(() => {
    if (id) {
      fetchSupplierData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isCustomer, viewParamsKey]);

  // Lazy load tab data
  useEffect(() => {
    if (activeTab === 'orders' && orders.length === 0 && supplier) {
      fetchOrders();
    } else if (activeTab === 'quality' && ncrs.length === 0 && supplier) {
      fetchNCRs();
    } else if (activeTab === 'users' && users.length === 0 && supplier) {
      fetchUsers();
    } else if ((activeTab === 'performance' || activeTab === 'settings') && !otifSettings && supplier) {
      fetchOtifSettings();
    }
  }, [activeTab, supplier]);

  const fetchSupplierData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await suppliersAPI.getProfile(Number(id), {
        view_level: viewLevel,
        ...getViewQueryParams(),
      });
      setSupplier(response.data);
      // CR-4815 r6: pull the full Company so the Overview card can render the
      // fields the slim company_details embed strips. Best-effort.
      void fetchCompanyInfo(response.data.company);
    } catch (err: unknown) {
      console.error('Error fetching supplier:', err);
      setError(getApiErrorMessage(err, 'Failed to load supplier details'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyInfo = async (companyId: number) => {
    // CR-4815 r6: read-only users without company-detail access get a 403
    // here — fall back to the slim company_details on the supplier profile
    // rather than surfacing an error (the card just shows fewer fields).
    try {
      const response = await coreAPI.getCompany(companyId);
      setCompanyInfo(response.data);
    } catch (err: unknown) {
      console.info('[CR-4815] full company fetch unavailable — using slim embed', err);
      setCompanyInfo(null);
    }
  };

  /**
   * Fetch one page of this supplier's orders.
   *
   * Previously capped at page_size=10 with no way forward: the tab showed the
   * ten most recent orders and a "View All" button that bounced to Purchasing.
   * A supplier with hundreds of POs was therefore unreadable here, and there
   * was no way to tell ten-of-ten from ten-of-four-hundred.
   *
   * @param page - 1-based page to load. Defaults to the current page.
   * @param supplierCode - CR-7028: Optional supplier code filter.
   */
  const fetchOrders = async (page: number = ordersPage, supplierCode?: string) => {
    if (!supplier) return;

    // CR-7028: Use provided supplierCode or fall back to current state
    const effectiveSupplierCode = supplierCode !== undefined ? supplierCode : ordersSupplierCode;

    try {
      setOrdersLoading(true);
      const params: Record<string, string | number> = {
        supplier: supplier.company,
        ordering: '-created_at',
        page,
        page_size: ORDERS_PAGE_SIZE,
      };
      // CR-7028: Add supplier code filter if provided
      if (effectiveSupplierCode) {
        params['supplier__supplier_code__icontains'] = effectiveSupplierCode;
      }
      const response = await purchasingAPI.getOrders(params);
      setOrders(response.data.results);
      // DRF returns the unpaginated total in `count` — that is what makes an
      // honest "x–y of N" possible instead of inferring from the page length.
      setOrdersCount(response.data.count ?? response.data.results.length);
      setOrdersPage(page);
    } catch (err: unknown) {
      console.error('Error fetching orders:', err);
    } finally {
      setOrdersLoading(false);
    }
  };

  const fetchNCRs = async () => {
    if (!supplier) return;

    try {
      setNCRsLoading(true);
      const response = await qualityAPI.getNCRs({
        supplier: supplier.company,
        ordering: '-created_at',
        page_size: 10,
      });
      setNCRs(response.data.results);
    } catch (err: unknown) {
      console.error('Error fetching NCRs:', err);
    } finally {
      setNCRsLoading(false);
    }
  };

  const fetchUsers = async () => {
    if (!supplier) return;

    try {
      setUsersLoading(true);
      const response = await coreAPI.getCompanyUsers(supplier.company);
      setUsers(response.data);
    } catch (err: unknown) {
      console.error('Error fetching users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchOtifSettings = async () => {
    if (!supplier) return;

    try {
      setOtifLoading(true);
      const response = await suppliersAPI.getOtifSettings(supplier.company);
      setOtifSettings({
        use_custom_otif_window: response.data.use_custom_otif_window,
        otif_days_early_allowed: response.data.otif_days_early_allowed,
        otif_days_late_allowed: response.data.otif_days_late_allowed,
      });
    } catch (err: unknown) {
      console.error('Error fetching OTIF settings:', err);
    } finally {
      setOtifLoading(false);
    }
  };

  const handleOpenUserModal = async (userProfile: UserProfile) => {
    setSelectedUser(userProfile);
    setUserModalTab('profile');
    setUserModalOpen(true);

    try {
      setPermissionsLoading(true);
      // CR-5344: Pass the supplier company id so the popup shows sites
      // scoped to THIS supplier's group (not the user's home group).
      // Multi-company users (Helix admins, COOs etc.) would otherwise
      // leak their other-tenant site memberships into this dialog.
      const response = await coreAPI.getUserSitePermissions(
        userProfile.id,
        supplier?.company ?? undefined
      );
      setUserSitePermissions(response.data);
    } catch (err: unknown) {
      console.error('Error fetching user permissions:', err);
      setUserSitePermissions([]);
    } finally {
      setPermissionsLoading(false);
    }
  };

  const handleCloseUserModal = () => {
    setUserModalOpen(false);
    setSelectedUser(null);
    setUserSitePermissions([]);
    setUserModalTab('profile');
  };

  const handleToggleSiteLink = async (siteId: number, isLinked: boolean) => {
    if (!selectedUser) return;

    setSavingPermission(siteId);
    try {
      const response = await coreAPI.updateUserSitePermission(selectedUser.id, siteId, {
        is_linked: isLinked,
        is_active: isLinked,
        can_purchasing: isLinked,
        can_quality: isLinked,
        can_finance: isLinked,
      });
      setUserSitePermissions((prev) =>
        prev.map((p) => (p.site === siteId ? response.data : p))
      );
    } catch (err: unknown) {
      console.error('Error updating site permission:', err);
    } finally {
      setSavingPermission(null);
    }
  };

  const handleUpdatePermission = async (siteId: number, field: string, value: boolean) => {
    if (!selectedUser) return;

    setSavingPermission(siteId);
    try {
      const response = await coreAPI.updateUserSitePermission(selectedUser.id, siteId, {
        [field]: value,
      });
      setUserSitePermissions((prev) =>
        prev.map((p) => (p.site === siteId ? response.data : p))
      );
    } catch (err: unknown) {
      console.error('Error updating permission:', err);
    } finally {
      setSavingPermission(null);
    }
  };

  const handleOtifSettingsUpdate = async (settings: Partial<OtifSettings>) => {
    if (!supplier) return;

    try {
      setOtifSaving(true);
      const response = await suppliersAPI.updateOtifSettings(supplier.company, settings);
      setOtifSettings({
        use_custom_otif_window: response.data.data.use_custom_otif_window,
        otif_days_early_allowed: response.data.data.otif_days_early_allowed,
        otif_days_late_allowed: response.data.data.otif_days_late_allowed,
      });
    } catch (err: unknown) {
      console.error('Error updating OTIF settings:', err);
    } finally {
      setOtifSaving(false);
    }
  };

  const handleUpdateProfile = async (data: Partial<SupplierProfile>) => {
    if (!supplier) return;
    const response = await suppliersAPI.updateProfile(supplier.company, data);
    setSupplier(response.data);
  };

  const handleUpdateCompany = async (data: Record<string, string | number | boolean | null>) => {
    if (!supplier) return;
    // CR-4815: send only the delta. Spreading supplier.company_details included
    // read-only/computed fields that the backend rejected, which caused the
    // Company PATCH to fail AFTER the SupplierProfile PATCH had already saved —
    // producing a partial update + misleading error toast.
    //
    // CR-4815 round 4: switched coreAPI.updateCompany (PUT) → coreAPI.patchCompany
    // (PATCH). The earlier rounds delivered a delta payload to a PUT endpoint,
    // and DRF's full-replace semantics rejected the request — Sarah's MAB-staging
    // bounce surfaced as a 403 on PUT /api/v1/core/companies/<id>/. PATCH is the
    // correct verb for partial updates; the backend's CompanyViewSet allows
    // PATCH from any company admin (CR-4819 wiring), so the delta now lands.
    await coreAPI.patchCompany(supplier.company, data as Partial<Company>);
    // Refresh supplier data to get updated company_details, and re-pull the
    // full Company so the Overview card reflects the saved values (CR-4815 r6:
    // the slim company_details embed never carries them).
    await fetchSupplierData();
    await fetchCompanyInfo(supplier.company);
  };

  const handleUploadLogo = async (file: File) => {
    if (!supplier) return;
    await coreAPI.uploadCompanyLogo(supplier.company, file);
    await fetchSupplierData();
  };

  const handleOpenEditUser = (userProfile: UserProfile) => {
    setEditUser(userProfile);
    setEditUserOpen(true);
  };

  const handleCloseEditUser = () => {
    setEditUserOpen(false);
    setEditUser(null);
  };

  const handleEditUserSaved = async () => {
    // CR-5857: refresh the list so the updated name/email/role propagate to
    // the cards immediately, and refresh the modal copy if it's still open
    // on the same user.
    await fetchUsers();
    if (selectedUser && editUser && selectedUser.id === editUser.id) {
      try {
        const refreshed = await coreAPI.getUserProfile(editUser.id);
        setSelectedUser(refreshed.data);
      } catch (err: unknown) {
        console.error('Error refreshing edited user:', err);
      }
    }
  };

  const handleResetPassword = async (userProfile: UserProfile) => {
    if (resettingPasswordFor === userProfile.id) return;
    try {
      setResettingPasswordFor(userProfile.id);
      const res = await coreAPI.adminResetUserPassword(userProfile.id);
      toast.success(res.data.message || `Password reset email sent to ${userProfile.user.email}`);
    } catch (err: unknown) {
      // CR-5857: same wrapped shape as EditUserDialog — message first, with
      // older keys as defensive fallbacks.
      const axiosErr = err as {
        response?: { data?: { error?: string; detail?: string; message?: string } };
      };
      const message =
        axiosErr.response?.data?.message ||
        axiosErr.response?.data?.error ||
        axiosErr.response?.data?.detail ||
        (err instanceof Error ? err.message : 'Failed to send password reset email');
      toast.error(message);
    } finally {
      setResettingPasswordFor(null);
    }
  };

  const handleImpersonate = async (userProfile: UserProfile) => {
    if (!isHelixAdmin && !isCustomer) return;

    try {
      setImpersonating(userProfile.user.id);
      const { authAPI } = await import('../../lib/api');
      // CR-4858 follow-up: pass view_company_id to the backend so it can
      // pin profile.current_company / current_site to the same company
      // the admin clicked through. Without this the backend would default
      // to the target user's home — which for multi-company users is
      // rarely what the admin intended (see CR-4858 description).
      const response = await authAPI.impersonate(
        userProfile.user.id,
        undefined,
        supplier?.company,
      );

      // API returns standardized format: { status, message, data: { target_user, target_token, original_token } }
      const impersonationData = response.data.data;

      // CR-4858: Pin the post-impersonation landing scope to *this* supplier
      // (the company the admin clicked through), not the impersonated user's
      // primary company. Multi-company users would otherwise land on whichever
      // company is stamped as their primary, which is rarely what the admin
      // intended when triaging from a specific supplier's Users tab.
      const contextCompany = supplier
        ? { id: supplier.company, name: supplier.company_name }
        : undefined;

      // Start impersonation (triggers full page reload)
      startImpersonation(
        impersonationData.target_user,
        impersonationData.target_token,
        impersonationData.original_token,
        contextCompany
      );
    } catch (err: unknown) {
      console.error('Error impersonating user:', err);
    } finally {
      setImpersonating(null);
    }
  };

  return {
    // State
    supplier,
    companyInfo,
    orders,
    ordersPage,
    ordersCount,
    ordersPageSize: ORDERS_PAGE_SIZE,
    ordersSupplierCode,
    ncrs,
    users,
    loading,
    ordersLoading,
    ncrsLoading,
    usersLoading,
    error,
    activeTab,
    selectedUser,
    userModalOpen,
    userModalTab,
    userSitePermissions,
    permissionsLoading,
    savingPermission,
    userSearch,
    impersonating,
    otifSettings,
    otifLoading,
    otifSaving,
    // CR-5857
    editUser,
    editUserOpen,
    resettingPasswordFor,

    // Actions
    fetchSupplierData,
    fetchOrders,
    fetchNCRs,
    fetchUsers,
    fetchOtifSettings,
    setActiveTab,
    handleOpenUserModal,
    handleCloseUserModal,
    handleToggleSiteLink,
    handleUpdatePermission,
    setUserSearch,
    handleOtifSettingsUpdate,
    setOtifSettings,
    handleUpdateProfile,
    handleUpdateCompany,
    handleUploadLogo,
    handleImpersonate,
    setUserModalTab,
    // CR-7028: Orders filter
    setOrdersSupplierCode,
    // CR-5857
    handleOpenEditUser,
    handleCloseEditUser,
    handleEditUserSaved,
    handleResetPassword,

    // Computed
    filteredUsers,
    isHelixAdmin,
    isCustomer,
    isCustomerAdmin,
    isOwnCompany,
  };
}
